import features from 'src/replicant/ruleset/features';
import { BlockId } from './block';
import { user } from 'src/StateObserver';

// goals
//-----------------------------------------------------------------------------
export type GoalId = BlockId;

// maps
//-----------------------------------------------------------------------------
// Empty properties are for blocks that are not in the game yet, so they don't have a goal icon
const goalMap: { [key in GoalId]: string } = {
  android: 'icon.android.png',
  animal: 'icon.animal.png',
  basicBlue: 'icon.basic.blue.png',
  basicGreen: 'icon.basic.green.png',
  basicPurple: 'icon.basic.purple.png',
  basicRed: 'icon.basic.red.png',
  basicYellow: 'icon.basic.yellow.png',
  bomb: 'icon.bomb.png',
  cage: 'icon.cage.front.png',
  candle: 'icon.candles.png',
  chain: 'icon.barbedwire.png',
  chameleon: 'icon.chameleon.png',
  coffin: '', // Goal is zombie
  cubeBlue: 'icon.cube.blue.png',
  cubeGreen: 'icon.cube.green.png',
  cubePurple: 'icon.cube.purple.png',
  cubeRed: 'icon.cube.red.png',
  cubeYellow: 'icon.cube.yellow.png',
  dog: 'icon.dog.png',
  doorAll: 'icon.door.all.png',
  doorBlue: 'icon.door.blue.png',
  doorGreen: 'icon.door.green.png',
  doorPurple: 'icon.door.purple.png',
  doorRed: 'icon.door.red.png',
  doorYellow: 'icon.door.yellow.png',
  firebox: 'icon.firebox.2.png',
  fireboxLollipop: 'icon.firebox.2.png',
  hatAll: 'icon.hat.all.png',
  hatBlue: 'icon.hat.blue.png',
  hatGreen: 'icon.hat.green.png',
  hatPurple: 'icon.hat.purple.png',
  hatRed: 'icon.hat.red.png',
  hatYellow: 'icon.hat.yellow.png',
  hazard: 'icon.hazard.png',
  kettle: 'icon.kettle.png',
  lock: 'icon.lock.1.png',
  pantherLarge: 'icon.panther.large.png',
  pantherSmall: 'icon.panther.small.png',
  pipe: 'icon.smoke.png',
  randomSpawn: '',
  rocketHorizontal: 'icon.rocket.horizontal.png',
  rocketVertical: 'icon.rocket.vertical.png',
  siren: 'icon.sirens.png',
  skull: 'icon.skull.png',
  slime: 'icon.slime.png',
  spider: 'icon.spider.png',
  fugu: '', // Goal is miniFugu
  miniFugu: 'icon.fugu.2.png',
  kappa: 'icon.kappa.png',
  statue: 'icon.statue.png',
  thrower: 'icon.thrower.png',
  virus: 'icon.hazard.png', // uses hazard icon
  wall: 'icon.wall.png',
  zombie: 'icon.zombie.png',
  ice: 'icon.ice.full.png',
  thickIce: '', // Goal is halfIce
  fullIce: '', // Goal is halfIce
  halfIce: 'icon.ice.half.png',
  worm: '', // Goal is butterfly
  cocoon: '', // Goal is butterfly
  butterfly: 'icon.butterfly.png',
  curtainAll: 'icon.curtain.all.png',
  curtainBlue: 'icon.curtain.blue.png',
  curtainGreen: 'icon.curtain.green.png',
  curtainPurple: 'icon.curtain.purple.png',
  curtainRed: 'icon.curtain.red.png',
  curtainYellow: 'icon.curtain.yellow.png',
  shell: 'icon.shell.png',
  pond: 'icon.pond.png',
  candyBag: '', // Goal is candy
  candy: 'icon.candy.png',
  sodaRed: 'icon.soda.png',
  sodaBlue: '', // Goal is sodaRed
  sodaGreen: '', // Goal is sodaRed
  sodaPurple: '', // Goal is sodaRed
  sodaYellow: '', // Goal is sodaRed
  bubbles: 'icon.bubbles.png',
  alarm: 'icon.alarm.png',
  chocolateEgg: 'icon.chocolateEgg.png',
  nest: 'icon.nest.png',
  mushroom: 'icon.mushroom.png',
  parcels: '', // Goal is hatAll
  fossil: 'icon.fossil.png',
  corn: '', // Goal is popcorn
  popcorn: 'icon.popcorn.png',
  tunaCan: '', // Goal is tunaFish
  tunaFish: 'icon.tunafish.png',
  rainbowBubble: 'icon.rainbowBubble.png',
  grass: 'icon.grass.png',
  firecrackerBox: 'icon.firecrackerBox.png',
  cooler: 'icon.cooler.png',
  tvAll: 'icon.tv.png',
  tvBlue: '', // Goal is tvAll
  tvGreen: '', // Goal is tvAll
  tvPurple: '', // Goal is tvAll
  tvRed: '', // Goal is tvAll
  tvYellow: '', // Goal is tvAll
  shieldBubble: 'icon.shieldBubble.png',
  bathtub: 'icon.bathtub.png',
  sharpenerLeft: 'icon.sharpener.png',
  sharpenerRight: '', // Goal is sharpenerLeft
  sharpenerTop: '', // Goal is sharpenerLeft
  sharpenerBottom: '', // Goal is sharpenerLeft
  barrel: 'icon.barrel.png',
  rainbowSoap: 'icon.rainbowSoap.png',
  bigEgg: 'icon.chick.png',
  piggyBank: 'icon.piggyBank.png',
  balloonBox: 'icon.balloonBox.png',
  octopus: 'icon.octopus.png',
  flowerFidget: 'icon.flowerFidget.png',
  magicBunnies: 'icon.magicBunnies.png',
  laserBoxHorizontal: 'icon.laserBox.horizontal.png',
  laserBoxVertical: 'icon.laserBox.png',
};

// Goal assets map variance for new first 2 obstacle visuals
const goalMap_newFirst2ObstacleVisuals: { [key in GoalId]: string } = {
  ...goalMap,
  doorAll: 'icon.dogHouse.all.png',
  doorBlue: 'icon.dogHouse.blue.png',
  doorGreen: 'icon.dogHouse.green.png',
  doorPurple: 'icon.dogHouse.purple.png',
  doorRed: 'icon.dogHouse.red.png',
  doorYellow: 'icon.dogHouse.yellow.png',
};

export function getGoalMap() {
  // If new first 2 obstacle visuals is enabled, use the new goal icons
  if (features(user()).newFirst2ObstacleVisuals) {
    return goalMap_newFirst2ObstacleVisuals;
  }

  return goalMap;
}

/**
 * For combining multi-sized goals:
 * Array of sets of goal IDs where the visual of the first one will be used and the amounts accumulate into, while the rest will be ignored
 */
export const multiSizeGoals: GoalId[][] = [
  // Milk Cartons
  ['pantherSmall', 'pantherLarge'],
  // Dog houses
  ['doorAll', 'doorBlue', 'doorGreen', 'doorPurple', 'doorRed', 'doorYellow'],
  // Parcels
  ['hatAll', 'hatBlue', 'hatGreen', 'hatPurple', 'hatRed', 'hatYellow'],
  // Curtains
  [
    'curtainAll',
    'curtainBlue',
    'curtainGreen',
    'curtainPurple',
    'curtainRed',
    'curtainYellow',
  ],
];
